import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { createFragmentContainer, RelayProp } from "react-relay";
// event-summary-time
import { eventTimeAndRecurrenceFragments } from "./EventTimeAndRecurrence.gql";
// util
import { ZonedMoment } from "@util/time-zone.util";
import {
  getAbsoluteDateTime,
  getAbsoluteDate,
} from "@clockwise/client-commons/src/util/format-time";
import { EFlex, getFlexFromEvent } from "@clockwise/client-commons/src/util/event-tag";
import { makeStyles } from "@material-ui/core";
import { greys, fontFamilyHelvetica } from "@clockwise/web-commons/src/styles";
import {
  getAutopilotTime,
  getEventTime,
} from "@clockwise/client-commons/src/util/event-recurrence";
import { isSmartHold } from "@src/util/event.util";
import { EventFlexibilityDescriptionDocument } from "./graphql/__generated__/EventFlexibilityDescription.generated";
import { useQuery } from "@apollo/client";
import { getValue } from "@clockwise/web-commons/src/util/errorable.util";
import { dayOfWeekExperimentIsOnForEvent } from "@clockwise/web-commons/src/ui/autopilot-flexibility/AutopilotFlexibility";
// material-ui

interface IContainer {
  org: ISchema.IOrg;
  event: ISchema.IEvent;
  startTime: ZonedMoment;
  endTime: ZonedMoment;
  unemphasized?: boolean;
  isAllDay?: boolean;
  selectedFlex?: EFlex;
  calendarId?: string;
}

interface IProps extends IContainer {
  relay: RelayProp;
}

const useStyles = makeStyles({
  wrapper: {
    marginBottom: 16,
  },
  body: {
    fontFamily: fontFamilyHelvetica,
    fontSize: `${14 / 16}rem`,
    color: greys.darkGreyBlue,
    lineHeight: `20px`,
  },
});

const EventTimeAndRecurrenceCmpt = ({
  event,
  startTime,
  endTime,
  isAllDay,
  selectedFlex,
  calendarId,
  org,
}: IProps) => {
  const classes = useStyles();
  /**
   * All day events have an end time of midnight on the next day, so we back it up to the previous day.
   * i.e. An all day event for Nov 1st will have an end time of midnight on Nov 2nd.
   */
  const endTimeAdjusted = endTime.clone().subtract(1, "minutes");
  const isSingleDay = startTime.isSame(endTimeAdjusted, "days");

  const formattedDate = isAllDay
    ? getAbsoluteDate(startTime, isSingleDay ? undefined : endTimeAdjusted, true)
    : getAbsoluteDateTime(startTime, endTime);

  const flexStringMap: { [key: string]: string } = {};

  // TODO: when do we need this?
  (
    (event.eventFlexibilityErrorable?.__typename === "EventFlexibilityNewList" &&
      event.eventFlexibilityErrorable.list) ||
    []
  ).forEach(({ flex: flexKey, flexString }) => (flexStringMap[flexKey] = flexString));

  const { data } = useQuery(EventFlexibilityDescriptionDocument, {
    variables: {
      calendarId: calendarId as string,
      CalendarId: calendarId as string,
      orgId: org.id as string,
      externalEventId: event.eventKey?.externalEventId as string,
    },
    skip: !calendarId || !org.id || !event.eventKey?.externalEventId,
  });
  const flexOrg = getValue(data?.org, "Org");
  const forceFetchEventParentErrorable = getValue(flexOrg?.forceFetchEventParentErrorable);
  const eventFlexibility = getValue(forceFetchEventParentErrorable?.flexibility);
  const eventFlexibilityConstraints = getValue(
    forceFetchEventParentErrorable?.flexibilityConstraints,
  );
  const dayOfWeekFlexibilityEligible = eventFlexibilityConstraints?.dayOfWeekFlexibilityEligible;
  const eventFlexibilityDescription = eventFlexibility?.description;
  const dayOfWeekOnForEvent = dayOfWeekExperimentIsOnForEvent(dayOfWeekFlexibilityEligible);

  const currentFlex = getFlexFromEvent(event, undefined);
  let recurrence: string | undefined = flexStringMap[selectedFlex || currentFlex] ?? "";

  if (!recurrence) {
    const flex = getFlexFromEvent(event, true); // flexibility from org-tag
    const eventTime = getEventTime(event); // the current recurrence string for event
    const autopilotTime = getAutopilotTime(event, selectedFlex || flex); // recurrence string accounting for autopilot flex
    recurrence =
      selectedFlex === undefined && flex === EFlex.Pin
        ? eventTime
        : selectedFlex === EFlex.Pin
        ? eventTime
        : dayOfWeekOnForEvent
        ? eventFlexibilityDescription || autopilotTime
        : autopilotTime;
  }

  return (
    <div className={classes.wrapper}>
      <p className={classes.body}>{formattedDate}</p>
      {event.recurrenceRules && !isSmartHold(event) && <p className={classes.body}>{recurrence}</p>}
    </div>
  );
};

export const EventTimeAndRecurrence = createFragmentContainer<IContainer>(
  EventTimeAndRecurrenceCmpt,
  eventTimeAndRecurrenceFragments,
);
